/**
 * The single source of every React Query key in the vendor portal.
 *
 * Why a factory rather than key literals at each call site: every wizard
 * mutation finishes with `invalidateQueries({ queryKey: ['me'] })`, which is a
 * PREFIX match. A key that does not start with 'me' is silently not matched —
 * no error, no warning, just data that never refreshes. That is exactly what
 * happened to the documents list, which was keyed ['/api/me/documents'] while
 * MyDocuments keyed the same URL ['me', 'documents']:
 *
 *   - two cache entries for one URL, so navigating between the two screens
 *     refetched it twice even inside staleTime, and
 *   - a vendor's own upload or reply left the wizard's copy stale until the
 *     component remounted.
 *
 * Two spellings of one URL are not a style inconsistency — they are two caches,
 * and prefix invalidation reaches only one of them. Keys therefore live here,
 * and meQueryKeys.test.ts asserts against TanStack's real matcher that every
 * one of them is reached by `all`.
 */

const ROOT = 'me';

export const meKeys = {
  /** The prefix every mutation invalidates. Also the company-profile key. */
  all: [ROOT] as const,

  /** GET /api/me — the vendor's own company row. */
  company: () => [ROOT] as const,

  /** GET /api/me/submissions */
  submissions: () => [ROOT, 'submissions'] as const,

  /** GET /api/me/questions */
  questions: () => [ROOT, 'questions'] as const,

  /** GET /api/me/documents */
  documents: () => [ROOT, 'documents'] as const,

  /** GET /api/me/questionnaires */
  questionnaires: () => [ROOT, 'questionnaires'] as const,

  /**
   * GET /api/me/question-categories, optionally scoped to one questionnaire.
   *
   * The id is OMITTED when absent rather than passed as null: unscoped is a
   * real request shape (the wizard fetches it when no questionnaire is
   * selected) and it is the same URL MyDocuments asks for. Keying it
   * [..., null] would have made a third cache entry for a response the portal
   * already holds.
   */
  questionCategories: (questionnaireId?: string | null) =>
    questionnaireId
      ? ([ROOT, 'question-categories', questionnaireId] as const)
      : ([ROOT, 'question-categories'] as const),

  /** A presigned preview URL for one uploaded file. */
  filePreview: (fileKey: string) => [ROOT, 'file-preview', fileKey] as const,

  /**
   * Per-submission comment thread and file history. Nested UNDER submissions so
   * that invalidating the submissions list also refreshes the threads hanging
   * off it — the shape the wizard already relied on.
   */
  comments: (submissionId: string) => [ROOT, 'submissions', submissionId, 'comments'] as const,
  filesHistory: (submissionId: string) =>
    [ROOT, 'submissions', submissionId, 'files-history'] as const,

  /**
   * Every thread / every file history for the company, in one entry each.
   *
   * These are what the questionnaire actually reads. The per-submission keys
   * above are deliberately NOT nested under these: they address a different URL
   * with a different response shape, and collapsing them would let a
   * single-submission fetch satisfy a batch read with one thread's data.
   */
  commentThreads: () => [ROOT, 'comment-threads'] as const,
  fileHistories: () => [ROOT, 'file-histories'] as const,
};
